import React, { ChangeEvent, InputHTMLAttributes, ReactNode } from 'react'
import { Field as FinalField } from 'react-final-form'
import styled from 'styled-components'
import { SpaceProps, ThemeColors } from '../../new/theme'
import { ScreenReaderOnly, Box, Flex, Text, Divider } from '../../new/core-components'

type Variant = 'core' | 'header' | 'item'

const StyledOuterRadioCircle = styled(Box)``
const StyledInnerRadioCircle = styled(Box)`
  transition: all 0.2s ease;
`

const StyledScreenReaderOnly = styled(ScreenReaderOnly)<{
  error: boolean
  radioColor: ThemeColors
  variant: Variant
}>`
  :not(:disabled) + label:hover {
    background: ${({ theme, variant }) => (variant === 'item' ? theme.colors['bg/major'] : 'none')};
  }

  // Show outline both on browsers supporting :focus-visible and only :focus
  :focus + label,
  :focus-visible + label {
    outline: auto 1px;
    // Shorthand breaks Firefox because -webkit-focus-ring-color is not valid there
    outline-color: -webkit-focus-ring-color;
  }

  // Disable :focus styles for browsers supporting :focus-visible
  :focus:not(:focus-visible) + label {
    outline: none;
  }

  :checked + label {
    font-weight: ${({ theme }) => theme.fontWeights[600]};

    background: ${({ theme, variant }) => (variant === 'item' ? theme.colors['bg/major'] : 'none')};

    ${StyledOuterRadioCircle} {
      border: 2px solid ${({ theme, radioColor }) => theme.colors[radioColor]};
      background: ${({ theme, radioColor }) => theme.colors[radioColor]};
    }

    ${StyledInnerRadioCircle} {
      opacity: 1;
      transform: scale(1);
    }
  }

  :not(:checked) + label {
    background: ${({ theme, variant, error }) => {
      if (error) {
        return theme.colors['bg/error-input-select']
      }

      return variant !== 'core' ? theme.colors['bg/page'] : undefined
    }};

    ${StyledOuterRadioCircle} {
      border-radius: 100%;
      border: 2px solid rgba(41, 52, 61, 0.48);
    }

    ${StyledInnerRadioCircle} {
      opacity: 0;
      transform: scale(0);
    }
  }

  :disabled + label {
    background: ${({ theme, variant }) =>
      variant !== 'core' ? theme.colors['bg/page'] : undefined};
    cursor: default;

    ${StyledOuterRadioCircle} {
      opacity: 0.5;
    }
  }
`

type Props = {
  addLeftBorder?: boolean
  checked?: boolean
  color?: ThemeColors
  'data-test-id'?: string
  defaultChecked?: boolean
  description?: string
  disabled?: boolean
  error?: string | boolean
  icon?: ReactNode
  id: string
  label: string
  labelSuffix?: string
  name: string
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  selectedContent?: ReactNode
  separator?: boolean
  variant?: Variant
} & InputHTMLAttributes<HTMLInputElement> &
  SpaceProps

/** @deprecated Use `Radio.Item` from `@chilipiper/design-system/src/new` instead */
export const Radio = ({
  onChange = () => undefined,
  id,
  name,
  addLeftBorder,
  checked,
  defaultChecked,
  disabled,
  label,
  labelSuffix,
  variant = 'item',
  value,
  w = '100%',
  px = 6,
  py = 4,
  description,
  separator = true,
  color = 'bg/select-indicator-major',
  'data-test-id': testId,
  icon,
  error = '',
  selectedContent,
  ...others
}: Props) => {
  const hasIcon = Boolean(icon)
  const getBoxShadow = () => {
    if (variant === 'item' && checked && addLeftBorder) {
      return 'border/section-selected'
    }
    return undefined
  }

  return (
    <Flex
      data-test-id={`radio-${id}`}
      data-checked={checked}
      w={w}
      boxShadow={getBoxShadow()}
      position='relative'
      {...others}
    >
      <StyledScreenReaderOnly
        as='input'
        variant={variant}
        type='radio'
        id={id}
        name={name}
        defaultChecked={defaultChecked}
        checked={checked}
        disabled={disabled}
        value={value}
        onChange={event => onChange(event)}
        data-test-id={testId}
        radioColor={color}
        aria-label={label}
        error={Boolean(error)}
        aria-describedby={description ? `${id}-description` : undefined}
      />

      <Box
        as='label'
        cursor='pointer'
        w='100%'
        py={py}
        px={px}
        mb={separator ? '1px' : undefined}
        fontWeight={500}
        htmlFor={id}
        data-test-id={testId ? `${testId}-label` : undefined}
      >
        <Flex alignItems='center'>
          <StyledOuterRadioCircle
            w={4}
            h={4}
            position='relative'
            borderRadius='full'
            flexShrink={0}
          >
            <StyledInnerRadioCircle
              w={2}
              h={2}
              position='absolute'
              top='2px'
              left='2px'
              bg='white'
              borderRadius='full'
            />
          </StyledOuterRadioCircle>
          {variant !== 'core' && (
            <Flex alignItems='center'>
              {hasIcon && (
                <Box lineHeight={0} ml={4}>
                  {icon}
                </Box>
              )}
              <Text
                ml={hasIcon ? 2 : 4}
                color={disabled ? 'text/action-disabled' : 'text/action-item'}
              >
                {label}
                {labelSuffix && (
                  <Text ml={2} color='text/disabled'>
                    {labelSuffix}
                  </Text>
                )}
              </Text>
            </Flex>
          )}
        </Flex>
        {variant !== 'core' && description && (
          <Box ml={7} mt={1}>
            <Text
              id={`${id}-description`}
              fontWeight={500}
              color={disabled ? 'text/action-disabled' : 'text/body-moderate'}
            >
              {description}
            </Text>
          </Box>
        )}
        {variant !== 'core' && checked && selectedContent && (
          <>
            <Divider mt={4} />
            <Box mt={4}>{selectedContent}</Box>
          </>
        )}
      </Box>
    </Flex>
  )
}

type FinalTextFieldProps = Omit<Props, 'name'> & { name: string }

/**
 * @deprecated Use `Radio.Item` from `@chilipiper/design-system/src/new` instead
 *
 *   To connect with `react-final-form` use `useRadioGroupField` hook from `@chilipiper/form`
 */
export const FinalRadioField = ({ name, value, onFocus, ...props }: FinalTextFieldProps) => {
  return (
    <FinalField name={name} value={value} type='radio'>
      {({ input }) => (
        <Radio
          {...props}
          {...input}
          onFocus={e => {
            onFocus?.(e)
            input.onFocus(e)
          }}
        />
      )}
    </FinalField>
  )
}
